import { APIClientError } from '@/services/Http/ApiClientError';
import type { HttpClient, IdempotentArgs, InvokeArgs, MultiCall, PostArgs } from '@/services/Http/HttpClient';
import { HTTPMethod, parseIdempotentArgs, parsePath, readResponseErrorMessage } from '@/services/Http/HttpClient';
import { getLoginRedirectUrl, objAsQueryString, removeUndefined } from '@/utils/siteUtils';
import { APIForbiddenError } from './APIForbiddenError';

export class BrowserHttpClient implements HttpClient {
    protected readonly abortSignal: AbortSignal;

    constructor(abortSignal?: AbortSignal) {
        this.abortSignal = abortSignal;
    }

    public async get<T = any>(args: IdempotentArgs): Promise<T | null> {
        // all browser calls must go via the NextJS API
        const invokeArgs = parseIdempotentArgs(args);
        let path = invokeArgs.path;
        if (!path.startsWith('/api/')) {
            path = `/api/get${path}`;
        }
        return this.invoke<T>({ ...invokeArgs, path });
    }

    public async post<T = any>(args: PostArgs): Promise<T | null> {
        // all browser calls must go via the NextJS API
        const { path, body, ...rest } = args;
        return this.invoke<T>({
            method: HTTPMethod.POST,
            path: '/api/csrf',
            body: {
                ...body,
                _url: path,
                _method: HTTPMethod.POST,
            },
            ...rest,
        });
    }

    public async delete<T = any>(args: IdempotentArgs): Promise<T | null> {
        // all browser calls must go via the NextJS API
        const { path, ...rest } = parseIdempotentArgs(args);
        return this.invoke<T>({
            ...rest,
            method: HTTPMethod.POST,
            path: '/api/csrf',
            body: {
                _url: path,
                _method: HTTPMethod.DELETE,
            },
        });
    }

    public async search<T = any>({ path, body, ...params }: PostArgs): Promise<T | null> {
        // all browser calls must go via the NextJS API
        return this.invoke({
            path: `/api/search${path}`,
            method: HTTPMethod.POST,
            body,
            ...params,
        });
    }

    public getAll(endpoints: MultiCall[]): Promise<Array<any>> {
        return Promise.all(this.mapMultiCall(endpoints));
    }

    public async getOptional(endpoints: MultiCall[]): Promise<Array<any>> {
        const results = await Promise.allSettled(this.mapMultiCall(endpoints));
        return results.map((result) => {
            if (result.status === 'fulfilled') {
                return result?.value;
            }
            console.info(`[BrowserHttpClient] error returned from getOptional: ${result.reason}`);
            return undefined;
        });
    }

    private async invoke<T = any>(
        { path, query, body, replacements, method = HTTPMethod.GET, keepalive }: InvokeArgs,
        retry = true
    ): Promise<T | null> {
        let fullPath = parsePath(path, replacements);
        if (fullPath === null) {
            return null; // Invalid path, treat as 404
        }
        const cleanQuery = query && removeUndefined(query);
        if (cleanQuery && Object.keys(cleanQuery).length > 0) {
            fullPath += (fullPath.includes('?') ? '&' : '?') + objAsQueryString(cleanQuery);
        }

        const options: any = {
            credentials: 'include',
            headers: { accept: 'application/json', 'content-type': 'application/json' },
        };
        options.method = method;
        if (this.abortSignal) {
            options.signal = this.abortSignal;
        }
        if (keepalive) {
            options.keepalive = true;
        }

        if (method === HTTPMethod.POST) {
            options.method = 'POST';
            if (body !== null) {
                options.body = JSON.stringify(body);
            }
        }

        try {
            return this.handleResponse(await fetch(fullPath, options));
        } catch (e) {
            if (e instanceof DOMException && e.name === 'AbortError') {
                // if the previous response was aborted, then we didn't want it anymore; quietly exit
                return null;
            }

            if (retry) {
                // retry the request once if it was interrupted
                console.info(`[BrowserHttpClient] retrying request: ${fullPath}`);
                return this.invoke<T>({ path, query, body, replacements, method }, false);
            }

            // throwing will provide the best information in the error log
            if (e instanceof APIClientError) {
                throw e;
            }

            throw new APIClientError(e.message, e?.response);
        }
    }

    private mapMultiCall(endpoints: MultiCall[]): Array<Promise<any>> {
        return endpoints.map(async (endpoint) => {
            if (!endpoint) {
                // no call was to be made, likely due to a false conditional.
                // ensure that null is assigned in the results array
                return null;
            }
            if (endpoint instanceof Promise) {
                return endpoint;
            }
            if (typeof endpoint === 'function') {
                return endpoint();
            }
            return this.get(parseIdempotentArgs(endpoint));
        });
    }

    private async handleResponse(response: Response) {
        // result was empty. Nothing to parse
        if (response.status === 404 || response.status === 204 || response.status === 205) {
            return null;
        }

        // the user is not logged in. Kick them out to login
        if (response.status === 401) {
            const params: Map<string, string> = new Map();
            try {
                const data = await response.json();
                if (data && data.expired) {
                    params.set('expired', '1');
                }
            } catch {
                // do nothing - not due to expired
            }
            return this.doBrowserRedirect(params);
        }

        // the current user is not allowed to access this resource. this would result in a full page failure or
        // an empty space, or a user message. It will depend on the context, so the result is thrown and the
        // consumer must handle it
        if (response.status === 403) {
            const message = await readResponseErrorMessage(response);
            throw new APIForbiddenError(message, response);
        }

        if (!response.ok) {
            const message = await readResponseErrorMessage(response);
            throw new APIClientError(message, response);
        }

        try {
            return await response.json();
        } catch (e) {
            // did the user get logged out through timeout or other tab?
            if (response.url && response.url.includes('/login')) {
                return await this.doBrowserRedirect();
            }
            throw new APIClientError(e.message, response);
        }
    }

    private doBrowserRedirect(params: Map<string, string> = new Map()) {
        const href = getLoginRedirectUrl(window.location.href, params);
        console.info(`[BrowserHttpClient] redirecting to ${href}`);
        window.location.href = href;
        return new Promise(() => {
            /**
             * We return a promise which never resolves here which a caller can await on.
             * This will halt the rest of the page trying to do render while the redirect is in progress
             */
        });
    }
}
